/**
 * Form Guard Utility
 * Detecta mudanças não salvas em formulários e exibe confirmação antes da navegação
 */

class FormGuard {
    constructor() {
        this.isDirty = false;
        this.isSubmitting = false;
        this.originalFormData = new Map();
        this.trackedForms = new Set();
        this.confirmationMessage = 'Você tem alterações não salvas. Deseja realmente sair desta página?';
        
        this.init();
    }

    /**
     * Inicializa o Form Guard
     */
    init() {
        const forms = document.querySelectorAll('form');
        
        // Verificar se a página contém apenas elementos de busca/filtro
        const hasOnlySearchElements = this.checkForSearchOnlyPage();
        
        // Form Guard: Verificando se deve ativar
        
        // Se a página contém apenas elementos de busca, não ativar
        if (hasOnlySearchElements) {
            // Form Guard: Página contém apenas elementos de busca/filtro - não ativado
            return;
        }
        
        // Só ativar se necessário
        if (!this.shouldActivate()) {
            // Form Guard: Não ativado para esta página
            return;
        }

        this.setupFormTracking();
        this.setupNavigationGuard();
        this.setupSubmitHandlers();
        
        // Form Guard: Ativado com sucesso para esta página
    }

    /**
     * Verifica se a página contém apenas elementos de busca/filtro
     */
    checkForSearchOnlyPage() {
        // Verificar se há formulários não relacionados à busca
        const allForms = document.querySelectorAll('form');
        const nonSearchForms = Array.from(allForms).filter(form => !this.shouldSkipForm(form));
        
        // Se não há formulários de dados, mas há inputs na página, ignorar verificação
        if (nonSearchForms.length === 0) {
            const allInputs = document.querySelectorAll('input, textarea, select');
            if (allInputs.length > 0) {
                // Form Guard: Página sem formulários mas com inputs - ignorando verificação
                return true;
            }
        }
        
        // Verificar se há elementos de busca sem formulários
        const searchInputs = document.querySelectorAll(
            'input[id*="search"], input[class*="search"], input[placeholder*="buscar"], ' +
            'input[placeholder*="pesquisar"], input[placeholder*="search"], ' +
            '.filters-page input, .filter input'
        );
        
        // Se há inputs de busca mas nenhum formulário de dados, considerar página de busca
        return searchInputs.length > 0 && nonSearchForms.length === 0;
    }

    /**
     * Verifica se deve ativar o Form Guard baseado na URL ou presença de formulários
     */
    shouldActivate() {
        const path = (window.testLocation && window.testLocation.pathname) || window.location.pathname;
        
        // URLs que nunca devem ter Form Guard
        const skipUrls = ['/login', '/logout', '/register', '/password'];
        if (skipUrls.some(url => path.includes(url))) {
            return false;
        }
        
        // Ativar APENAS em rotas de criação e edição
        if (path.includes('/create') || path.includes('/edit')) {
            return true;
        }
        
        // NÃO ativar em outras páginas, mesmo que tenham formulários
        // O Form Guard deve ser usado apenas em páginas de criação/edição
        return false;
    }

    /**
     * Configura o rastreamento de formulários
     */
    setupFormTracking() {
        // Aguarda o DOM estar pronto
        if (document.readyState === 'loading') {
            document.addEventListener('DOMContentLoaded', () => this.trackForms());
        } else {
            this.trackForms();
        }
    }

    /**
     * Rastreia todos os formulários na página
     */
    trackForms() {
        const forms = document.querySelectorAll('form');
        
        forms.forEach(form => {
            // Pular formulários de busca ou outros que não devem ser rastreados
            if (this.shouldSkipForm(form)) {
                return;
            }

            this.trackForm(form);
        });
    }

    /**
     * Verifica se deve pular o rastreamento de um formulário
     */
    shouldSkipForm(form) {
        const skipClasses = [
            'search-form', 'filter-form', 'no-guard', 'login-form', 'logout-form', 'search', 'filter',
            'filters-page', 'filter--appointment'
        ];
        const skipIds = [
            'search', 'filter', 'login', 'logout', 'loginForm', 'logoutForm', 
            'searchClient', 'searchService', 'searchEmployee', 'searchVehicle', 'searchServiceOrder',
            'searchGarage', 'searchSupplier'
        ];
        const skipActions = ['login', 'logout', 'search', 'filter', 'appointment.search'];
        
        // Verificar classes
        if (skipClasses.some(className => form.classList.contains(className))) {
            return true;
        }
        
        // Verificar IDs
        if (skipIds.includes(form.id)) {
            return true;
        }
        
        // Verificar action do formulário
        const action = form.getAttribute('action') || '';
        if (skipActions.some(skipAction => action.includes(skipAction))) {
            return true;
        }
        
        // Verificar se tem atributo data-no-guard
        if (form.hasAttribute('data-no-guard')) {
            return true;
        }
        
        // Verificar se é um formulário muito simples (apenas 1-2 campos)
        const inputs = form.querySelectorAll('input[type="text"], input[type="email"], input[type="password"], textarea, select');
        if (inputs.length <= 2) {
            // Se tem poucos campos, verificar se são campos de busca/filtro
            const hasSearchKeywords = Array.from(inputs).some(input => {
                const name = (input.name || '').toLowerCase();
                const placeholder = (input.placeholder || '').toLowerCase();
                const id = (input.id || '').toLowerCase();
                const className = (input.className || '').toLowerCase();
                return name.includes('search') || name.includes('filter') || 
                       placeholder.includes('buscar') || placeholder.includes('pesquisar') || placeholder.includes('filtrar') ||
                       placeholder.includes('search') || placeholder.includes('filter') ||
                       id.includes('search') || id.includes('filter') ||
                       className.includes('search') || className.includes('filter');
            });
            if (hasSearchKeywords) {
                return true;
            }
        }
        
        // Verificar se o formulário contém apenas campos de busca/filtro independente do número de campos
        const allInputs = form.querySelectorAll('input, select, textarea');
        const searchInputs = Array.from(allInputs).filter(input => {
            const name = (input.name || '').toLowerCase();
            const placeholder = (input.placeholder || '').toLowerCase();
            const id = (input.id || '').toLowerCase();
            const className = (input.className || '').toLowerCase();
            const type = (input.type || '').toLowerCase();
            
            // Verificar palavras-chave em diferentes atributos
            const searchKeywords = [
                'search', 'filter', 'buscar', 'pesquisar', 'filtrar', 'pesquise'
            ];
            
            const hasSearchKeyword = searchKeywords.some(keyword => 
                name.includes(keyword) || placeholder.includes(keyword) || 
                id.includes(keyword) || className.includes(keyword)
            );
            
            // Verificar IDs específicos de busca
            const searchIds = [
                'searchclient', 'searchservice', 'searchemployee', 'searchvehicle', 
                'searchserviceorder', 'searchgarage', 'searchsupplier'
            ];
            
            const hasSearchId = searchIds.some(searchId => 
                id.includes(searchId) || className.includes(searchId)
            );
            
            // Verificar se é campo de data em formulário de filtro
            const isDateFilter = type === 'date' && (
                name.includes('date') || name.includes('start') || name.includes('end') ||
                placeholder.includes('data') || placeholder.includes('período')
            );
            
            return hasSearchKeyword || hasSearchId || isDateFilter;
        });
        
        // Se todos os inputs são de busca/filtro, ignorar o formulário
        if (searchInputs.length === allInputs.length && allInputs.length > 0) {
            return true;
        }
        
        // Verificar se o formulário tem botões típicos de busca/filtro
        const buttons = form.querySelectorAll('button, input[type="submit"]');
        const hasSearchButtons = Array.from(buttons).some(button => {
            const text = (button.textContent || button.value || '').toLowerCase();
            const className = (button.className || '').toLowerCase();
            return text.includes('buscar') || text.includes('filtrar') || text.includes('pesquisar') ||
                   text.includes('search') || text.includes('filter') || text.includes('limpar') ||
                   className.includes('clear-filter');
        });
        
        // Se tem botões de busca e poucos campos, provavelmente é formulário de busca
        if (hasSearchButtons && allInputs.length <= 5) {
            return true;
        }
        
        return false;
    }

    /**
     * Rastreia um formulário específico
     */
    trackForm(form) {
        if (this.trackedForms.has(form)) {
            return;
        }

        // Armazenar dados originais do formulário
        this.storeOriginalFormData(form);
        
        // Adicionar listeners para detectar mudanças
        this.addFormChangeListeners(form);
        
        this.trackedForms.add(form);
        // Formulário adicionado ao rastreamento
    }

    /**
     * Armazena os dados originais do formulário
     */
    storeOriginalFormData(form) {
        const formData = new FormData(form);
        const originalData = new Map();
        
        // Armazenar valores de inputs, selects e textareas
        const elements = form.querySelectorAll('input, select, textarea');
        elements.forEach(element => {
            if (element.type === 'checkbox' || element.type === 'radio') {
                originalData.set(element.name || element.id, element.checked);
            } else {
                originalData.set(element.name || element.id, element.value);
            }
        });
        
        this.originalFormData.set(form, originalData);
    }

    /**
     * Adiciona listeners para detectar mudanças no formulário
     */
    addFormChangeListeners(form) {
        const elements = form.querySelectorAll('input, select, textarea');
        
        elements.forEach(element => {
            // Diferentes eventos para diferentes tipos de elementos
            const events = this.getEventsForElement(element);
            
            events.forEach(event => {
                element.addEventListener(event, () => {
                    this.checkFormChanges(form);
                });
            });
        });
    }

    /**
     * Retorna os eventos apropriados para cada tipo de elemento
     */
    getEventsForElement(element) {
        const type = element.type;
        
        if (type === 'checkbox' || type === 'radio') {
            return ['change'];
        } else if (element.tagName === 'SELECT') {
            return ['change'];
        } else {
            return ['input', 'change'];
        }
    }

    /**
     * Verifica se houve mudanças no formulário
     */
    checkFormChanges(form) {
        if (this.isSubmitting) {
            return;
        }

        const originalData = this.originalFormData.get(form);
        if (!originalData) {
            return;
        }

        const elements = form.querySelectorAll('input, select, textarea');
        let hasChanges = false;
        
        for (const element of elements) {
            const key = element.name || element.id;
            const originalValue = originalData.get(key);
            let currentValue;
            
            if (element.type === 'checkbox' || element.type === 'radio') {
                currentValue = element.checked;
            } else {
                currentValue = element.value;
            }
            
            if (originalValue !== currentValue) {
                hasChanges = true;
                break;
            }
        }
        
        this.isDirty = hasChanges;
    }

    /**
     * Configura a proteção contra navegação
     */
    setupNavigationGuard() {
        // Proteção contra fechamento da aba/janela
        window.addEventListener('beforeunload', (e) => {
            if (this.isDirty && !this.isSubmitting) {
                e.preventDefault();
                e.returnValue = this.confirmationMessage;
                return this.confirmationMessage;
            }
        });

        // Proteção contra navegação via links
        this.setupLinkInterception();
    }

    /**
     * Intercepta cliques em links para mostrar confirmação
     */
    setupLinkInterception() {
        document.addEventListener('click', (e) => {
            if (!this.isDirty || this.isSubmitting) {
                return;
            }

            // Verificar se o clique foi em um botão de submit ou dentro de um formulário
            const submitButton = e.target.closest('button[type="submit"], input[type="submit"]');
            const form = e.target.closest('form');
            
            // Se clicou em um botão de submit, não interceptar
            if (submitButton) {
                // Marcar como submetendo para evitar interferência
                this.isSubmitting = true;
                this.isDirty = false;
                return;
            }

            // Se clicou em um botão dentro de um formulário rastreado, verificar se é um botão de salvar
            if (form && this.trackedForms.has(form)) {
                const button = e.target.closest('button');
                if (button) {
                    // Verificar se é um botão de salvar/submit baseado no texto ou classes
                    const buttonText = button.textContent.toLowerCase().trim();
                    const buttonClass = button.className.toLowerCase();
                    const buttonId = button.id.toLowerCase();
                    
                    const saveKeywords = ['salvar', 'save', 'enviar', 'submit', 'confirmar', 'gravar', 'cadastrar', 'atualizar'];
                    const isSaveButton = saveKeywords.some(keyword => 
                        buttonText.includes(keyword) || 
                        buttonClass.includes(keyword) || 
                        buttonId.includes(keyword)
                    );
                    
                    if (isSaveButton) {
                        // Marcar como submetendo para evitar interferência
                        this.isSubmitting = true;
                        this.isDirty = false;
                        return;
                    }
                }
            }

            const link = e.target.closest('a');
            if (!link || !link.href) {
                return;
            }

            // Pular links que abrem em nova aba ou são âncoras
            if (link.target === '_blank' || link.href.startsWith('#') || link.href.startsWith('javascript:')) {
                return;
            }

            // Pular links internos da mesma página
            if (link.href === window.location.href) {
                return;
            }

            e.preventDefault();
            
            if (confirm(this.confirmationMessage)) {
                this.isDirty = false; // Resetar para permitir navegação
                window.location.href = link.href;
            }
        });
    }

    /**
     * Configura handlers para submissão de formulários
     */
    setupSubmitHandlers() {
        document.addEventListener('submit', (e) => {
            const form = e.target;
            
            if (this.trackedForms.has(form)) {
                this.isSubmitting = true;
                this.isDirty = false;
                
                // Formulário sendo submetido, Form Guard desativado temporariamente
            }
        });
    }

    /**
     * Força a verificação de mudanças em todos os formulários rastreados
     */
    checkAllForms() {
        this.trackedForms.forEach(form => {
            this.checkFormChanges(form);
        });
    }

    /**
     * Reseta o estado do Form Guard
     */
    reset() {
        this.isDirty = false;
        this.isSubmitting = false;
        this.trackedForms.forEach(form => {
            this.storeOriginalFormData(form);
        });
    }

    /**
     * Marca o formulário como limpo (sem mudanças)
     */
    markAsClean() {
        this.isDirty = false;
        this.trackedForms.forEach(form => {
            this.storeOriginalFormData(form);
        });
    }

    /**
     * Verifica se há mudanças não salvas
     */
    hasUnsavedChanges() {
        return this.isDirty;
    }

    /**
     * Define uma mensagem personalizada de confirmação
     */
    setConfirmationMessage(message) {
        this.confirmationMessage = message;
    }

    /**
     * Adiciona um formulário específico ao rastreamento
     */
    addForm(formSelector) {
        const form = document.querySelector(formSelector);
        if (form && !this.shouldSkipForm(form)) {
            this.trackForm(form);
        }
    }

    /**
     * Remove um formulário do rastreamento
     */
    removeForm(formSelector) {
        const form = document.querySelector(formSelector);
        if (form && this.trackedForms.has(form)) {
            this.trackedForms.delete(form);
            this.originalFormData.delete(form);
        }
    }

    /**
     * Destrói o Form Guard e remove todos os listeners
     */
    destroy() {
        this.trackedForms.clear();
        this.originalFormData.clear();
        this.isDirty = false;
        this.isSubmitting = false;
        
        // Form Guard destruído
    }
}

// Instância global do Form Guard
let formGuardInstance = null;

/**
 * Inicializa o Form Guard globalmente
 */
function initFormGuard() {
    if (!formGuardInstance) {
        formGuardInstance = new FormGuard();
    }
    return formGuardInstance;
}

/**
 * Obtém a instância atual do Form Guard
 */
function getFormGuard() {
    return formGuardInstance;
}

// Auto-inicialização
document.addEventListener('DOMContentLoaded', () => {
    initFormGuard();
});

// Se o DOM já estiver carregado
if (document.readyState !== 'loading') {
    initFormGuard();
}

// Exportar para uso em outros módulos
export { FormGuard, initFormGuard, getFormGuard };

// Disponibilizar globalmente
window.FormGuard = FormGuard;
window.initFormGuard = initFormGuard;
window.getFormGuard = getFormGuard;
